import { useCallback, useState } from "react";

export type ColorModePreference = "system" | "light" | "dark";

const STORAGE_KEY = "mtn-cctv-color-mode";

function readStored(): ColorModePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    // Private browsing / storage disabled: fall through to the default.
  }
  return "system";
}

// Persists the user's explicit light/dark/system choice across reloads.
// "system" is the default and is not itself written back on first render —
// only an explicit choice is, so a user who never touches the toggle keeps
// following their OS preference indefinitely, including if it changes later.
export function useColorMode(): [ColorModePreference, (mode: ColorModePreference) => void] {
  const [preference, setPreference] = useState<ColorModePreference>(readStored);

  const setAndStore = useCallback((mode: ColorModePreference) => {
    setPreference(mode);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Best-effort: the in-memory state still updates for this session.
    }
  }, []);

  return [preference, setAndStore];
}
